/**
 * DV-DEP-001: Deployments Page
 *
 * Main deployments page component that lists finished productions,
 * provides download functionality, and YouTube integration.
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Filter, SortAsc, SortDesc, RefreshCw, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useProductions, useProductionsPagination } from '@/hooks/useDeployments';
import YoutubeIntegrationCard from '@/components/deployments/YoutubeIntegrationCard';
import ProductionList from '@/components/deployments/ProductionList';
import UploadToYouTubeModal from '@/components/deployments/UploadToYouTubeModal';
import ScheduleToYouTubeModal from '@/components/deployments/ScheduleToYouTubeModal';
import JobStatusPanel from '@/components/deployments/JobStatusPanel';
import type { Production, ProductionFilters, ProductionSort } from '@/types/deployments';

const DeploymentsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProduction, setSelectedProduction] = useState<Production | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showJobsModal, setShowJobsModal] = useState(false);

  // Pagination and filtering
  const {
    params,
    setFilters,
    setSort,
    nextPage,
    prevPage,
  } = useProductionsPagination(20);

  // Fetch productions
  const {
    data: productionsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useProductions(params);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setFilters({
      ...params.filters,
      search: query || undefined,
    });
  };

  // Handle sort change
  const handleSortChange = (field: ProductionSort['field']) => {
    const currentSort = params.sort;
    const isCurrentField = currentSort?.field === field;
    const newDirection = isCurrentField && currentSort.direction === 'desc' ? 'asc' : 'desc';

    setSort({
      field,
      direction: newDirection,
    });
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<ProductionFilters>) => {
    setFilters({
      ...params.filters,
      ...newFilters,
    });
  };

  // Handle YouTube upload actions
  const handleUploadNow = (production: Production) => {
    setSelectedProduction(production);
    setShowUploadModal(true);
  };

  const handleScheduleUpload = (production: Production) => {
    setSelectedProduction(production);
    setShowScheduleModal(true);
  };

  const handleViewJobs = (production: Production) => {
    setSelectedProduction(production);
    setShowJobsModal(true);
  };

  // Close modals
  const closeModals = () => {
    setShowUploadModal(false);
    setShowScheduleModal(false);
    setShowJobsModal(false);
    setSelectedProduction(null);
  };

  // Calculate pagination info
  const totalProductions = productionsData?.total || 0;
  const currentPage = Math.floor((params.offset || 0) / (params.limit || 20));
  const totalPages = Math.ceil(totalProductions / (params.limit || 20));
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  return (
    <>
      <Helmet>
        <title>Deployments - HI-SI Dashboard</title>
        <meta name="description" content="Manage finished video productions and YouTube deployments" />
      </Helmet>

      <div className="min-h-screen bg-black text-white p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-light tracking-wider text-cyan-400">
                Deployments
              </h1>
              <p className="text-white/60 mt-2 font-mono text-sm">
                Manage finished productions and YouTube uploads
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className={cn(
                  'px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 rounded-lg',
                  'text-cyan-400 hover:bg-cyan-500/30 transition-colors',
                  'flex items-center space-x-2 font-mono text-sm',
                  isFetching && 'opacity-50 cursor-not-allowed'
                )}
              >
                <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* YouTube Integration Card */}
          <YoutubeIntegrationCard />

          {/* Search and Filters */}
          <div className="bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="text"
                    placeholder="Search productions..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-black/60 border border-cyan-500/20 rounded-lg
                             text-white placeholder-white/40 focus:border-cyan-500/40 focus:outline-none
                             font-mono text-sm"
                  />
                </div>
              </div>

              {/* Sort Controls */}
              <div className="flex items-center space-x-2">
                <span className="text-white/60 font-mono text-sm">Sort by:</span>
                {(['created_at', 'title', 'duration'] as const).map((field) => {
                  const isActive = params.sort?.field === field;
                  const direction = params.sort?.direction;

                  return (
                    <button
                      key={field}
                      onClick={() => handleSortChange(field)}
                      className={cn(
                        'px-3 py-1 rounded border text-sm font-mono transition-colors flex items-center space-x-1',
                        isActive
                          ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400'
                          : 'bg-black/40 border-white/10 text-white/60 hover:border-white/20'
                      )}
                    >
                      <span>{field === 'created_at' ? 'Date' : field === 'title' ? 'Title' : 'Duration'}</span>
                      {isActive && (
                        direction === 'desc' ? (
                          <SortDesc className="w-3 h-3" />
                        ) : (
                          <SortAsc className="w-3 h-3" />
                        )
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={cn(
                  'px-4 py-2 rounded border font-mono text-sm transition-colors flex items-center space-x-2',
                  showFilters
                    ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400'
                    : 'bg-black/40 border-white/10 text-white/60 hover:border-white/20'
                )}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Aspect Ratio Filter */}
                  <div>
                    <label className="block text-white/60 font-mono text-sm mb-2">
                      Aspect Ratio
                    </label>
                    <select
                      value={params.filters?.aspectRatio || ''}
                      onChange={(e) => handleFilterChange({
                        aspectRatio: e.target.value as any || undefined
                      })}
                      className="w-full px-3 py-2 bg-black/60 border border-cyan-500/20 rounded-lg
                               text-white font-mono text-sm focus:border-cyan-500/40 focus:outline-none"
                    >
                      <option value="">All</option>
                      <option value="16:9">Landscape (16:9)</option>
                      <option value="9:16">Portrait (9:16)</option>
                      <option value="1:1">Square (1:1)</option>
                    </select>
                  </div>

                  {/* Date Range Filters */}
                  <div>
                    <label className="block text-white/60 font-mono text-sm mb-2">
                      Date From
                    </label>
                    <input
                      type="date"
                      value={params.filters?.dateFrom || ''}
                      onChange={(e) => handleFilterChange({
                        dateFrom: e.target.value || undefined
                      })}
                      className="w-full px-3 py-2 bg-black/60 border border-cyan-500/20 rounded-lg
                               text-white font-mono text-sm focus:border-cyan-500/40 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-white/60 font-mono text-sm mb-2">
                      Date To
                    </label>
                    <input
                      type="date"
                      value={params.filters?.dateTo || ''}
                      onChange={(e) => handleFilterChange({
                        dateTo: e.target.value || undefined
                      })}
                      className="w-full px-3 py-2 bg-black/60 border border-cyan-500/20 rounded-lg
                               text-white font-mono text-sm focus:border-cyan-500/40 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Productions List */}
          {isError ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
              <p className="text-red-400 font-mono">
                Failed to load productions: {error?.message || 'Unknown error'}
              </p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg
                         text-red-400 hover:bg-red-500/30 transition-colors font-mono text-sm"
              >
                Retry
              </button>
            </div>
          ) : (
            <ProductionList
              productions={productionsData?.productions || []}
              isLoading={isLoading}
              onUploadNow={handleUploadNow}
              onScheduleUpload={handleScheduleUpload}
              onViewJobs={handleViewJobs}
            />
          )}

          {/* Pagination */}
          {totalProductions > 0 && (
            <div className="flex items-center justify-between bg-black/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg p-4">
              <div className="text-white/60 font-mono text-sm">
                Showing {Math.min((params.offset || 0) + 1, totalProductions)} - {Math.min((params.offset || 0) + (params.limit || 20), totalProductions)} of {totalProductions} productions
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={prevPage}
                  disabled={!hasPrevPage}
                  className={cn(
                    'px-3 py-1 rounded border font-mono text-sm transition-colors',
                    hasPrevPage
                      ? 'bg-black/40 border-white/20 text-white hover:border-white/40'
                      : 'bg-black/20 border-white/10 text-white/40 cursor-not-allowed'
                  )}
                >
                  Previous
                </button>

                <span className="text-white/60 font-mono text-sm px-2">
                  {currentPage + 1} / {totalPages}
                </span>

                <button
                  onClick={nextPage}
                  disabled={!hasNextPage}
                  className={cn(
                    'px-3 py-1 rounded border font-mono text-sm transition-colors',
                    hasNextPage
                      ? 'bg-black/40 border-white/20 text-white hover:border-white/40'
                      : 'bg-black/20 border-white/10 text-white/40 cursor-not-allowed'
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showUploadModal && selectedProduction && (
        <UploadToYouTubeModal
          production={selectedProduction}
          onClose={closeModals}
        />
      )}

      {showScheduleModal && selectedProduction && (
        <ScheduleToYouTubeModal
          production={selectedProduction}
          onClose={closeModals}
        />
      )}

      {showJobsModal && selectedProduction && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-cyan-500/30 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-cyan-500/20">
              <h2 className="text-xl font-medium text-cyan-400">
                Deployment Jobs: {selectedProduction.title}
              </h2>
              <button
                onClick={closeModals}
                className="text-white/60 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              <JobStatusPanel
                productionId={selectedProduction.id}
                className="border-0"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeploymentsPage;